import * as React from 'react';

import { ORDER_SUMMARY_LABEL } from './constants';
import type { MemberOrder } from './types';

/**
 * **The order summary. One component, rendered on checkout and on the receipt** (`MEM-020` AC-3).
 *
 * ## Why this is a component and not a block in each screen
 *
 * AC-3 is *"checkout and the receipt itemise **identically**, in the same order and with the same
 * labels"*. The way that claim dies is not that somebody decides the two screens should differ —
 * it is that somebody edits one of them six weeks later, in good faith, and nothing says the other
 * exists. Two `<dl>`s with the same rows are two places to make that edit.
 *
 * So there is one. `ORG-017` AC-4 established the pattern in this repository — the organizer's
 * live preview and their public profile render from one `OrganizerProfileCard`, for exactly this
 * reason — and the organizer console shares message copy between two screens through a single
 * constant on the same argument.
 *
 * `one-itemisation-two-screens.test.tsx` holds both halves of it: a source scan proving both
 * screens import this component and nothing else draws a line, and a render of the two screens
 * from one order asserting the two itemisation regions are byte-identical.
 *
 * ## It renders what it is given, and decides nothing
 *
 * No branch on a zero amount (`MEM-020` AC-5, `MEM-050` AC-1 — under a zero-rate configuration
 * the Service fee line reads $0.00 and is *shown*; hiding a zero is the instinct that has to be
 * designed out rather than remembered). Whether a free order carries a fee line at all is the
 * server's decision, made from whether a ticket was paid for and never from the amount, and this
 * component draws the lines it is handed. No formatting: `line.amount` is the server's own rendering, produced by integer
 * arithmetic in `formatCents`, so this file never turns cents into a string and a test may assert
 * the bytes. No filtering, no sorting, no summing — the lines arrive in the order they are drawn
 * and the total is one of them.
 *
 * The only thing it decides is which row is emphasised, and it reads that from `kind` rather than
 * from the position or the label, so a line added on the server does not make the wrong row
 * bold.
 *
 * ## Markup
 *
 * A `<dl>`, because that is what a list of labelled amounts is: `aria-label` names the region and
 * each pair is a term and its definition, so a screen reader announces "Service fee, $0.00"
 * rather than eight loose strings (`NFR accessibility#wcag-aa`).
 *
 * ## It carries no surface of its own, and that is the fix rather than the shortcut
 *
 * It used to draw the card — `bg-card`, a hairline border, `p-4`. `Web App.dc.html` §3.1 does not
 * put the lines in a card: they are the **bottom half of the order-summary card**, under the
 * event's own date and name and separated from them by a `--divider` rule. So every caller was
 * either nesting a card inside a card or undoing this one — `order-receipt-document-page.tsx` was
 * literally passing `className="border-0 p-0"` to take it back off, which is the shape of a
 * component drawing something that is not its business.
 *
 * So the lines are the lines, and the four callers each supply the surface their screen wants.
 * `one-itemisation-two-screens.test.tsx` compares checkout's rendering with the receipt's byte for
 * byte, and neither passes a `className`, so the thing that has to be identical still is.
 */
export function OrderItemisation({
  order,
  className,
}: {
  readonly order: MemberOrder;
  readonly className?: string;
}): React.ReactElement {
  return (
    <dl aria-label={ORDER_SUMMARY_LABEL} data-testid="order-itemisation" className={className}>
      {order.lines.map((line) => (
        <div
          key={line.kind}
          /*
            The total's ink is `text-foreground`. It used to name a class the preset does not
            register, so it compiled to nothing and the one line the summary exists to emphasise
            took its neighbours' colour — §3.1 draws it as the only strong line in the card.
          */
          className={
            line.kind === 'total'
              ? 'mt-2.5 flex items-baseline justify-between border-t border-divider pt-2.5 font-brand text-md font-extrabold text-foreground'
              : 'mb-2 flex items-baseline justify-between text-sm text-text-body last:mb-0'
          }
        >
          <dt>{line.label}</dt>
          <dd>{line.amount}</dd>
        </div>
      ))}
    </dl>
  );
}
